import { useEffect, useMemo, useState } from "react";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { LangstonParameterSchema } from "../../LangstonParameterSchema";

interface UseStandardsQueryOptions {
  searchParams?: URLSearchParams;
  getSearchParamValues?: (key: string) => string[];
}

export const useStandardsQuery = (options?: UseStandardsQueryOptions) => {
  const [queryHasStandardsParam, setQueryHasStandardsParam] = useState(false);

  let searchParams: URLSearchParams;
  let getSearchParamValues: (key: string) => string[];

  //In order to avoid errors when the search page context is not available, we need to provide fallback values
  try {
    // on Try will be used the search page context and continue the original flow
    const context = useSearchPageContext();
    searchParams = context.getters.searchParams;
    getSearchParamValues = context.handlers.getSearchParamValues;
  } catch {
    // on catch will be used the options provided avoiding errors to break the flow
    searchParams = options?.searchParams || new URLSearchParams();
    getSearchParamValues = options?.getSearchParamValues || (() => []);
  }

  const searchParamsObj = useMemo(
    () => (searchParams ? Object.fromEntries(searchParams.entries()) : {}),
    [searchParams],
  );

  const standardsInQuery = useMemo(
    () => getSearchParamValues(LangstonParameterSchema.standards.key) || [],
    [getSearchParamValues, JSON.stringify(searchParamsObj)],
  );

  const fetchParamsForQuery = useMemo(() => {
    if (standardsInQuery.length > 0) {
      return { metadata_ids: standardsInQuery, ...searchParamsObj };
    }
    if (queryHasStandardsParam) {
      return searchParamsObj;
    }
    return undefined;
  }, [standardsInQuery, queryHasStandardsParam, searchParamsObj]);

  useEffect(() => {
    // Checks necessity to toggle the queryHasStandardsParam
    setQueryHasStandardsParam(standardsInQuery.length > 0);
  }, [standardsInQuery.length]);

  return {
    fetchParamsForQuery,
    searchParamsObj,
    standardsInQuery,
  };
};
